// Engagements.jsx

// Dependencies
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTable } from 'react-table';

// Components
import EngagementNotes from '@/components/data/engagements/EngagementNotes';
import HoursDisplay from '@/components/data/hours/HoursDisplay';
import MondayDate from '@/components/data/date/MondayDate';

// Styles
import { professionalengagementsCss } from '@digital_monorepo/styling';

const ProfessionalEngagements = ({ engagements, startDates, isEngagementView = false }) => {
  const { t } = useTranslation();

  // Generate unique dates for current week + 1 year
  const uniqueDates = useMemo(() => {
    if (!startDates || startDates.length === 0) return [];

    // Get current week start (Monday) - moved back by 1 week
    const today = new Date();
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(today.getDate() - daysToMonday - 7); // Subtract additional 7 days to start one week earlier
    startOfWeek.setHours(0, 0, 0, 0);

    // Get end date (52 weeks from start of current week)
    const endDate = new Date(startOfWeek);
    endDate.setDate(endDate.getDate() + (52 * 7)); // 52 weeks = 364 days (1 year)

    const dates = Array.from(new Set(startDates.map(entry => entry.StartDate)))
      .filter(dateString => {
        const date = new Date(dateString);
        return date >= startOfWeek && date <= endDate;
      })
      .sort();

    return dates.map(date => MondayDate.formatToUIDate(date));
  }, [startDates]);

  // Calculate weekly totals for conditional styling
  const weeklyTotals = useMemo(() => {
    const totals = {};
    uniqueDates.forEach(date => {
      totals[date] = 0;
    });

    engagements.forEach(entry => {
      const formattedDate = MondayDate.formatToUIDate(entry.StartDate);
      if (totals.hasOwnProperty(formattedDate)) {
        totals[formattedDate] += Number(entry.Hours || 0);
      }
    });

    return totals;
  }, [engagements, uniqueDates]);

  // Helper function to get background color based on weekly total
  const getWeekBackgroundColor = (weekDate) => {
    const total = weeklyTotals[weekDate] || 0;
    
    if (total === 0) {
      return '#ffcccc'; // Red for no allocation
    } else if (total < 16) {
      return '#ffe6e6'; // Light red for under utilization
    } else if (total < 40) {
      return '#fff4cc'; // Light yellow for partial utilization
    } else if (total === 40) {
      return '#d4edda'; // Light green for full utilization
    } else {
      return '#d4edda'; // Light green for over utilization (>40)
    }
  };

  // Group data by Professional/Engagement ID and map data
  const tableData = useMemo(() => {
    const groupedData = {};
    engagements.forEach(entry => {
      const id = isEngagementView ? entry.ProfessionalID : entry.EngagementID;
      const { Hours, StartDate } = entry;

      if (!groupedData[id]) {
        if (isEngagementView) {
          groupedData[id] = {
            ProfessionalID: entry.ProfessionalID,
            ProfessionalName: entry.ProfessionalName,
            FunctionName: entry.FunctionName,
            Office: entry.Office,
            ProfessionalRC: entry.ProfessionalRC,
            ProfessionalNotes: entry.ProfessionalNotes,
            Hours: {},
          };
        } else {
          groupedData[id] = {
            EngagementID: entry.EngagementID,
            EngagementName: entry.EngagementName,
            Client: entry.Client,
            ClientCode: entry.ClientCode,
            Type: entry.Type,
            Status: entry.Status,
            Manager: entry.Manager,
            EngagementRC: entry.EngagementRC,
            EngagementNotes: entry.EngagementNotes,
            Hours: {},
          };
        }
      }

      groupedData[id].Hours[MondayDate.formatToUIDate(StartDate)] = Hours;
    });

    return Object.values(groupedData);
  }, [engagements, isEngagementView]);

  // Helper function to parse DD/MM/YYYY format
  const parseUIDate = (dateString) => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper function to format date as "DD-MMM"
  const formatWeekHeader = (dateString) => {
    const date = parseUIDate(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    const month = monthNames[date.getMonth()];
    
    return `${day}-${month}`;
  };

  // Helper function to get week number of the month
  const getWeekOfMonth = (dateString) => {
    const date = parseUIDate(dateString);
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = date.getDate();
    return Math.ceil((dayOfMonth + firstDayOfMonth.getDay()) / 7);
  };

  // Define columns based on view type
  const columns = useMemo(() => {
    const baseColumns = isEngagementView ? [
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_ID'),
        accessor: 'ProfessionalID',
        sticky: 'left',
        width: 150,
        Cell: ({ value }) => <Link to={`/professionals/${value}`} className={professionalengagementsCss.linkCell}>{value}</Link>,
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_NAME'),
        accessor: 'ProfessionalName',
        sticky: 'left',
        width: 200,
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.FUNCTION_NAME'),
        accessor: 'FunctionName',
        width: 150,
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.OFFICE'),
        accessor: 'Office',
        width: 120,
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_RC'),
        accessor: 'ProfessionalRC',
        width: 150,
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.HOURS'),
        accessor: 'Hours',
        width: 80,
        Cell: ({ row }) => {
          const totalHours = Object.values(row.original.Hours).reduce((sum, hours) => sum + Number(hours), 0);
          return <HoursDisplay hours={totalHours} />;
        }
      }
    ] : [
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_ID'),
        accessor: 'EngagementID',
        sticky: 'left',
        width: 150,
        Cell: ({ value }) => <Link to={`/engagements/${value}`} className={professionalengagementsCss.linkCell}>{value}</Link>,
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_NAME'),
        accessor: 'EngagementName',
        sticky: 'left',
        width: 200,
        Cell: ({ value }) => (
          <div className={professionalengagementsCss.engagementNameCell} title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.CLIENT'),
        accessor: 'Client',
        width: 120,
        Cell: ({ value }) => (
          <div className={professionalengagementsCss.truncatedCell} title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.CLIENT_CODE'),
        accessor: 'ClientCode',
        width: 150,
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.TYPE'),
        accessor: 'Type',
        width: 100,
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.STATUS'),
        accessor: 'Status',
        width: 120,
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.MANAGER'),
        accessor: 'Manager',
        width: 150,
        Cell: ({ value }) => value ? (
          <Link to={`/professionals/${value}`} className={professionalengagementsCss.linkCell}>
            <div className={professionalengagementsCss.truncatedCell} title={value}>
              {value}
            </div>
          </Link>
        ) : '',
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_RC'),
        accessor: 'EngagementRC',
        width: 120,
      },
      {
        Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_NOTES'),
        accessor: 'EngagementNotes',
        width: 200,
        Cell: ({ row }) => (
          <EngagementNotes
            engagementID={row.original.EngagementID}
            engagementNotes={row.original.EngagementNotes}
          />
        ),
      },
    ];

    // Add date columns with formatted headers
    const dateColumns = uniqueDates.map((date, index) => {
      const currentDate = parseUIDate(date);
      const currentWeek = Math.ceil((currentDate.getDate() + new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay()) / 7);
      
      return {
        Header: formatWeekHeader(date),
        accessor: `Hours.${date}`,
        width: 80,
        Cell: ({ value }) => value ? <HoursDisplay hours={value} /> : null,
        originalDate: date,
      };
    });

    return [...baseColumns, ...dateColumns];
  }, [t, uniqueDates, isEngagementView]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: tableData });

  const totalEngagements = tableData.length;
  const totalHours = tableData.reduce((sum, row) => {
    const hours = Object.values(row.Hours || {}).reduce((h, val) => h + Number(val || 0), 0);
    return sum + hours;
  }, 0);

  if (totalEngagements === 0) {
    return (
      <div className={professionalengagementsCss.sectionContainer}>
        <div className={professionalengagementsCss.sectionHeader}>
          <h3 className={professionalengagementsCss.sectionTitle}>
            {isEngagementView ? t('PROFESSIONALS_TABLE.TITLE') : t('ENGAGEMENTS_TABLE.TITLE')}
          </h3>
        </div>
        <div className={professionalengagementsCss.tableWrapper}>
          <div className={professionalengagementsCss.emptyState}>
            <p className={professionalengagementsCss.emptyText}>
              {isEngagementView ? 'No professionals found' : 'No engagements found'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={professionalengagementsCss.sectionContainer}>
      <div className={professionalengagementsCss.sectionHeader}>
        <h3 className={professionalengagementsCss.sectionTitle}>
          {isEngagementView ? t('PROFESSIONALS_TABLE.TITLE') : t('ENGAGEMENTS_TABLE.TITLE')}
        </h3>
        <div className={professionalengagementsCss.tableStats}>
          <span className={professionalengagementsCss.statBadge}>
            {totalEngagements} {isEngagementView ? 'professionals' : 'engagements'}
          </span>
          <span className={professionalengagementsCss.statBadge}>
            {totalHours}h total
          </span>
          <span className={professionalengagementsCss.statBadge}>
            {uniqueDates.length} weeks
          </span>
        </div>
      </div>
      
      <div className={professionalengagementsCss.tableWrapper}>
        <div className={professionalengagementsCss.tableContainer}>
          <table {...getTableProps()} className={professionalengagementsCss.table}>
            <thead>
              {/* Year Row */}
              <tr className={professionalengagementsCss.headerRow}>
                {headerGroups[0].headers.map((column, colIndex) => {
                  const isDateColumn = column.accessor && typeof column.accessor === 'string' && column.accessor.startsWith('Hours.');
                  
                  if (!isDateColumn) {
                    return (
                      <th
                        key={colIndex}
                        rowSpan={3}
                        className={`${professionalengagementsCss.th} ${professionalengagementsCss.thYear} ${column.sticky === 'left' ? `${professionalengagementsCss.sticky} ${professionalengagementsCss.headerSticky}` : ''}`}
                      >
                        {column.render('Header')}
                      </th>
                    );
                  }
                  
                  const date = column.originalDate;
                  const currentYear = parseUIDate(date).getFullYear();
                  const prevColumn = headerGroups[0].headers[colIndex - 1];
                  const prevDate = prevColumn && prevColumn.originalDate ? prevColumn.originalDate : null;
                  const prevYear = prevDate ? parseUIDate(prevDate).getFullYear() : null;
                  
                  if (prevYear === currentYear) {
                    return null;
                  }
                  
                  let yearSpan = 1;
                  for (let i = colIndex + 1; i < headerGroups[0].headers.length; i++) {
                    const nextCol = headerGroups[0].headers[i];
                    if (nextCol.originalDate) {
                      const nextDate = nextCol.originalDate;
                      const nextYear = parseUIDate(nextDate).getFullYear();
                      if (nextYear === currentYear) {
                        yearSpan++;
                      } else {
                        break;
                      }
                    }
                  }
                  
                  return (
                    <th
                      key={colIndex}
                      colSpan={yearSpan}
                      className={`${professionalengagementsCss.th} ${professionalengagementsCss.thYear}`}
                    >
                      {currentYear}
                    </th>
                  );
                })}
              </tr>
              
              {/* Month Row */}
              <tr className={professionalengagementsCss.headerRow}>
                {headerGroups[0].headers.map((column, colIndex) => {
                  const isDateColumn = column.accessor && typeof column.accessor === 'string' && column.accessor.startsWith('Hours.');
                  
                  if (!isDateColumn) {
                    return null;
                  }
                  
                  const date = column.originalDate;
                  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                  const currentMonth = monthNames[parseUIDate(date).getMonth()];
                  const currentYear = parseUIDate(date).getFullYear();
                  const currentMonthNum = parseUIDate(date).getMonth();
                  
                  const prevColumn = headerGroups[0].headers[colIndex - 1];
                  const prevDate = prevColumn && prevColumn.originalDate ? prevColumn.originalDate : null;
                  const prevMonth = prevDate ? parseUIDate(prevDate).getMonth() : null;
                  const prevYear = prevDate ? parseUIDate(prevDate).getFullYear() : null;
                  
                  if (prevMonth === currentMonthNum && prevYear === currentYear) {
                    return null;
                  }
                  
                  let monthSpan = 1;
                  for (let i = colIndex + 1; i < headerGroups[0].headers.length; i++) {
                    const nextCol = headerGroups[0].headers[i];
                    if (nextCol.originalDate) {
                      const nextDate = nextCol.originalDate;
                      const nextMonth = parseUIDate(nextDate).getMonth();
                      const nextYear = parseUIDate(nextDate).getFullYear();
                      if (nextMonth === currentMonthNum && nextYear === currentYear) {
                        monthSpan++;
                      } else {
                        break;
                      }
                    }
                  }
                  
                  return (
                    <th
                      key={colIndex}
                      colSpan={monthSpan}
                      className={`${professionalengagementsCss.th} ${professionalengagementsCss.thMonth}`}
                    >
                      {currentMonth}
                    </th>
                  );
                })}
              </tr>
              
              {/* Week Row */}
              <tr className={professionalengagementsCss.headerRow}>
                {headerGroups[0].headers.map((column, colIndex) => {
                  const isDateColumn = column.accessor && typeof column.accessor === 'string' && column.accessor.startsWith('Hours.');
                  
                  if (!isDateColumn) {
                    return null;
                  }
                  
                  const date = column.originalDate;
                  const weekNum = getWeekOfMonth(date);
                  const isFirstWeek = colIndex === 0 || !headerGroups[0].headers[colIndex - 1].originalDate;
                  
                  return (
                    <th
                      key={colIndex}
                      className={`${professionalengagementsCss.th} ${professionalengagementsCss.thWeek} ${isFirstWeek ? professionalengagementsCss.weekBorderLeft : ''}`}
                    >
                      {weekNum}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, rowIndex) => {
                prepareRow(row);
                return (
                  <tr 
                    {...row.getRowProps()} 
                    key={rowIndex}
                    className={professionalengagementsCss.row}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const isDateColumn = Boolean(cell.column.originalDate);
                      const isFirstWeek = isDateColumn && (cellIndex === 0 || !row.cells[cellIndex - 1].column.originalDate);
                      
                      return (
                        <td
                          {...cell.getCellProps()}
                          key={cellIndex}
                          className={`${professionalengagementsCss.td} ${cell.column.sticky === 'left' ? professionalengagementsCss.sticky : ''} ${isDateColumn ? professionalengagementsCss.hoursCell : ''} ${isFirstWeek ? professionalengagementsCss.weekBorderLeft : ''}`}
                          style={isDateColumn ? { backgroundColor: getWeekBackgroundColor(cell.column.originalDate) } : undefined}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalEngagements;
